import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="activity-selector"
export default class extends Controller {
  static targets = ["list"]
  static values = {
    currentPhase: { type: Number, default: 1 },
    currentActivity: { type: Number, default: 0 },
    activities: Array
  }

  connect() {
    this.selectedActivities = []
    // Store activity metadata to maintain selection across ID changes
    this.selectedActivityMetadata = new Map() // Map of activityId -> {title, phaseId, phaseNumber}

    // Listen for activity list changes (add, delete, reorder)
    this.boundHandleActivityListChanged = this.handleActivityListChanged.bind(this)
    window.addEventListener('activity-list-changed', this.boundHandleActivityListChanged)

    // Listen for activity removed from file-upload controller
    this.boundHandleActivityRemoved = this.handleActivityRemoved.bind(this)
    window.addEventListener('file-upload:activityRemoved', this.boundHandleActivityRemoved)

    // Listen for preloaded activities from file-upload controller
    this.boundHandleActivityPreloaded = this.handleActivityPreloaded.bind(this)
    window.addEventListener('file-upload:activityPreloaded', this.boundHandleActivityPreloaded)

    // Reconstruct metadata from existing chips in the file-upload controller
    this.reconstructMetadataFromChips()

    // Track dropdown state by observing the menu visibility
    this.observeDropdownState()
  }

  // Reconstruct selected activity metadata from existing chips
  reconstructMetadataFromChips() {
    // Find the file-upload controller's fileList element
    const activityCard = this.element.closest('[data-activity-card]')
    if (!activityCard) return

    const fileListElement = activityCard.querySelector('[data-file-upload-target="fileList"]')
    if (!fileListElement) return

    // Find all activity chips
    const activityChips = fileListElement.querySelectorAll('[data-activity-chip]')

    activityChips.forEach(chip => {
      const activityId = chip.dataset.activityId
      const title = chip.querySelector('span.font-semibold')?.textContent
      const phaseSpan = chip.querySelector('span.text-xs')
      const phaseNumber = phaseSpan ? phaseSpan.textContent.replace('Phase ', '') : ''

      if (activityId && title) {
        this.selectedActivities.push(activityId)
        this.selectedActivityMetadata.set(activityId, {
          title: title,
          phaseNumber: phaseNumber
        })
      }
    })
  }

  disconnect() {
    window.removeEventListener('activity-list-changed', this.boundHandleActivityListChanged)
    window.removeEventListener('file-upload:activityRemoved', this.boundHandleActivityRemoved)
    window.removeEventListener('file-upload:activityPreloaded', this.boundHandleActivityPreloaded)

    if (this.dropdownObserver) {
      this.dropdownObserver.disconnect()
    }
  }

  // Handle preloaded activity from file-upload controller
  handleActivityPreloaded(event) {
    const activity = event.detail.activity

    // Add to selected activities
    if (!this.selectedActivities.includes(activity.id)) {
      this.selectedActivities.push(activity.id)
      this.selectedActivityMetadata.set(activity.id, {
        title: activity.title,
        phaseNumber: activity.phaseNumber
      })
    }
  }

  // Observe dropdown state changes
  observeDropdownState() {
    // Find the dropdown menu element
    const dropdownMenu = this.element.querySelector('[data-dropdown-target="menu"]')
    if (!dropdownMenu) return

    // Use MutationObserver to watch for attribute changes (hidden attribute)
    this.dropdownObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'hidden') {
          const isOpen = !dropdownMenu.hasAttribute('hidden')

          // Track when dropdown closes
          if (!isOpen && this.wasDropdownOpen) {
            this.handleDropdownClosed()
          }

          this.wasDropdownOpen = isOpen
        }
      })
    })

    this.dropdownObserver.observe(dropdownMenu, {
      attributes: true,
      attributeFilter: ['hidden']
    })

    // Set initial state
    this.wasDropdownOpen = !dropdownMenu.hasAttribute('hidden')
  }

  // Called when dropdown is closed
  handleDropdownClosed() {
    // Hook for future functionality
  }

  // Handle activity removed from file upload (user clicked X button on chip)
  handleActivityRemoved(event) {
    const activityId = event.detail.activityId
    const metadata = this.selectedActivityMetadata.get(activityId)

    // Remove from selected array - always do this even if checkbox not found
    this.selectedActivities = this.selectedActivities.filter(id => id !== activityId)
    this.selectedActivityMetadata.delete(activityId)

    // Try to find and uncheck the checkbox if dropdown is loaded
    if (this.hasListTarget) {
      // Try to find and uncheck the checkbox using current activityId first
      let checkbox = this.listTarget.querySelector(`[data-activity-id="${activityId}"] input[type="checkbox"]`)

      // If not found by ID and we have metadata, try to find by title and phase
      if (!checkbox && metadata) {
        // Find activity in current dropdown by matching title and phase
        const activityElements = this.listTarget.querySelectorAll('[data-activity-id]')

        for (const element of activityElements) {
          const elementTitle = element.dataset.activityTitle
          const elementPhaseNumber = element.dataset.phaseNumber

          if (elementTitle === metadata.title &&
              elementPhaseNumber === metadata.phaseNumber) {
            checkbox = element.querySelector('input[type="checkbox"]')
            // Also update our selected array to remove this new ID
            const newActivityId = element.dataset.activityId
            this.selectedActivities = this.selectedActivities.filter(id => id !== newActivityId)
            this.selectedActivityMetadata.delete(newActivityId)
            break
          }
        }
      }

      if (checkbox) {
        checkbox.checked = false
      }
    }
  }

  // Load activities when dropdown opens
  loadActivities() {
    // Let the dropdown component handle the toggle
    // Just load the activities list
    setTimeout(() => {
      const allActivities = this.getPreviousActivities()

      if (allActivities.length === 0) {
        this.renderEmptyState()
        return
      }

      this.renderActivitiesList(allActivities)
    }, 0)
  }

  // Handle activity list changes (add, delete, reorder)
  handleActivityListChanged() {
    // Get current valid activities
    const currentValidActivities = this.getPreviousActivities()

    // Check if any selected activities are no longer valid (deleted or out of range)
    const invalidSelections = []

    this.selectedActivityMetadata.forEach((metadata, activityId) => {
      const key = `${metadata.title}|${metadata.phaseNumber || ''}`
      const stillExists = currentValidActivities.some(
        activity => `${activity.title}|${activity.phaseNumber || ''}` === key
      )

      if (!stillExists) {
        invalidSelections.push(activityId)
      }
    })

    // Remove invalid selections and dispatch events to remove chips
    invalidSelections.forEach(activityId => {
      const metadata = this.selectedActivityMetadata.get(activityId)

      this.selectedActivities = this.selectedActivities.filter(id => id !== activityId)
      this.selectedActivityMetadata.delete(activityId)

      // Dispatch window event to remove the chip from input with both ID and metadata
      // Using window.dispatchEvent instead of this.dispatch to ensure event reaches file-upload controller
      window.dispatchEvent(new CustomEvent('activity-selector:activityDeselected', {
        detail: {
          activityId: activityId,
          metadata: metadata // Include metadata so chip can be found by title+phase if needed
        },
        bubbles: true
      }))
    })

    // If dropdown is currently open, refresh the list
    if (this.wasDropdownOpen) {
      this.refreshDropdown()
    }
  }

  // Refresh the dropdown list
  refreshDropdown() {
    const allActivities = this.getPreviousActivities()

    if (allActivities.length === 0) {
      this.renderEmptyState()
      return
    }

    // Update the activity IDs in selectedActivities array to match new structure
    this.updateSelectedActivityIds(allActivities)

    this.renderActivitiesList(allActivities)
  }

  // Update selected activity IDs after list changes
  updateSelectedActivityIds(allActivities) {
    // Create a map of activity identifiers (title + phaseNumber) to new activity objects
    const activityMap = new Map()

    allActivities.forEach(activity => {
      const key = `${activity.title}|${activity.phaseNumber || ''}`
      activityMap.set(key, activity)
    })

    // Update selectedActivities array and metadata with new IDs
    const updatedSelectedActivities = []
    const updatedMetadata = new Map()

    // Iterate through metadata to find matching activities
    this.selectedActivityMetadata.forEach((metadata) => {
      const key = `${metadata.title}|${metadata.phaseNumber || ''}`
      const newActivity = activityMap.get(key)

      if (newActivity) {
        updatedSelectedActivities.push(newActivity.id)
        updatedMetadata.set(newActivity.id, {
          title: metadata.title,
          phaseNumber: metadata.phaseNumber
        })
      }
    })

    this.selectedActivities = updatedSelectedActivities
    this.selectedActivityMetadata = updatedMetadata
  }

  // Prevent dropdown from closing when clicking inside
  preventClose(event) {
    event.stopPropagation()
  }

  // Get preloaded activities from file-upload controller (for expand mode)
  getPreloadedActivities() {
    const activities = []

    // Find the file-upload controller element
    const fileUploadElement = this.element.closest('[data-controller*="file-upload"]')

    if (!fileUploadElement) {
      return activities
    }

    // Get the preloaded activities data from the file-upload controller
    const preloadedActivitiesData = fileUploadElement.dataset.fileUploadPreloadedActivitiesValue

    if (!preloadedActivitiesData) {
      return activities
    }

    try {
      const preloadedActivities = JSON.parse(preloadedActivitiesData)

      // Transform preloaded activities to match the expected format
      preloadedActivities.forEach((activity, index) => {
        activities.push({
          id: activity.id.toString(),
          phaseId: activity.phaseNumber ? activity.phaseNumber : null,
          phaseName: activity.phaseNumber ? `Phase ${activity.phaseNumber}` : 'Activities',
          phaseNumber: activity.phaseNumber,
          activityIndex: index,
          activityNumber: activity.activityNumber,
          title: activity.title,
          type: activity.type,
          badge: activity.badge,
          icon: null, // Icon not provided in preloaded data
          description: null // Description not provided in preloaded data
        })
      })
    } catch (e) {
      console.error('Failed to parse preloaded activities:', e)
    }

    return activities
  }

  // Get all activities from all phases
  getPreviousActivities() {
    if (this.hasActivitiesValue) {
      return this.activitiesValue.map(activity => ({ ...activity, id: String(activity.id),
        phaseId: activity.phaseNumber, phaseName: activity.phaseName || 'Activities' }))
    }
    const activities = []

    // Find the current activity card (the one containing this selector)
    const currentActivityCard = this.element.closest('[data-activity-card]')

    // Check if we're in expand/full-screen mode (no phase containers in DOM)
    const allPhases = document.querySelectorAll('[data-phase-id]')
    const isExpandMode = allPhases.length === 0 && currentActivityCard

    // If in expand mode, check if we have preloaded activities from the server
    if (isExpandMode) {
      return this.getPreloadedActivities()
    }

    // Get current activity's phase and number
    let currentPhaseNumber = null
    let currentActivityNumber = null
    if (currentActivityCard) {
      const currentNumberElement = currentActivityCard.querySelector('[data-activity-number]')
      currentActivityNumber = currentNumberElement ? parseInt(currentNumberElement.textContent) : null

      // Try to find which phase the current activity is in
      const currentPhase = currentActivityCard.closest('[data-phase-id]')
      if (currentPhase) {
        const allPhasesArray = Array.from(document.querySelectorAll('[data-phase-id]'))
        currentPhaseNumber = allPhasesArray.indexOf(currentPhase) + 1
      }
    }

    const isSinglePhase = allPhases.length <= 1

    if (allPhases.length > 0) {
      // Multi-phase layout: organize by phases
      allPhases.forEach((phaseContainer, phaseIndex) => {
        const phaseId = parseInt(phaseContainer.dataset.phaseId)
        // Extract just the phase name (e.g., "Discovery" from "Phase 1. Discovery")
        const phaseFullText = phaseContainer.querySelector('.font-semibold.text-base')?.textContent.trim() || `Phase ${phaseId}`
        const phaseName = phaseFullText.replace(/^Phase\s+\d+\.\s*/, '') || phaseFullText
        // Use actual phase position from database instead of DOM index
        const phaseNumber = phaseContainer.dataset.phasePosition ? parseInt(phaseContainer.dataset.phasePosition) + 1 : phaseIndex + 1

        // Get all activities in this phase
        const activityCards = phaseContainer.querySelectorAll('[data-activity-card]')

        activityCards.forEach((card, cardIndex) => {
          // Skip the current activity card itself
          if (card === currentActivityCard) {
            return
          }

          const activityNumberElement = card.querySelector('[data-activity-number]')
          const activityNumber = activityNumberElement ? parseInt(activityNumberElement.textContent) : cardIndex + 1
          const activityTitle = card.dataset.activityTitle
          const activityType = card.dataset.activityType
          const activityBadge = card.dataset.activityBadge
          const activityIcon = card.dataset.activityIcon
          const activityDescription = card.dataset.activityDescription

          // Skip activities in the same phase that have number >= current activity
          if (currentPhaseNumber !== null && currentActivityNumber !== null &&
              phaseNumber === currentPhaseNumber && activityNumber >= currentActivityNumber) {
            return
          }

          // Skip all activities in phases after the current phase
          if (currentPhaseNumber !== null && phaseNumber > currentPhaseNumber) {
            return
          }

          // Include activities from previous phases and previous activities in current phase
          // Get actual activity ID from data attribute
          const actualActivityId = card.dataset.activityId

          activities.push({
            id: actualActivityId || `phase-${phaseId}-activity-${cardIndex}`,
            phaseId: phaseId,
            phaseName: phaseName,
            phaseNumber: isSinglePhase ? null : phaseNumber,
            activityIndex: cardIndex,
            activityNumber: activityNumber,
            title: activityTitle || `Activity ${activityNumber}`,
            type: activityType,
            badge: activityBadge,
            icon: activityIcon,
            description: activityDescription
          })
        })
      })
    } else {
      // Single layout: no phases, just get all activity cards directly
      let currentActivityNumber = null
      if (currentActivityCard) {
        const currentNumberElement = currentActivityCard.querySelector('[data-activity-number]')
        currentActivityNumber = currentNumberElement ? parseInt(currentNumberElement.textContent) : null
      }

      const activityCards = document.querySelectorAll('[data-activity-card]')

      activityCards.forEach((card, cardIndex) => {
        // Skip the current activity card itself
        if (card === currentActivityCard) {
          return
        }

        const activityNumberElement = card.querySelector('[data-activity-number]')
        const activityNumber = activityNumberElement ? parseInt(activityNumberElement.textContent) : cardIndex + 1
        const activityTitle = card.dataset.activityTitle
        const activityType = card.dataset.activityType
        const activityBadge = card.dataset.activityBadge
        const activityIcon = card.dataset.activityIcon
        const activityDescription = card.dataset.activityDescription

        // Only include activities with number less than current activity
        if (currentActivityNumber !== null && activityNumber >= currentActivityNumber) {
          return
        }

        // Include all activities without phase info
        // Get actual activity ID from data attribute
        const actualActivityId = card.dataset.activityId

        activities.push({
          id: actualActivityId || `activity-${cardIndex}`,
          phaseId: null,
          phaseName: 'Activities', // Default group name for single layout
          phaseNumber: null,
          activityIndex: cardIndex,
          activityNumber: activityNumber,
          title: activityTitle || `Activity ${activityNumber}`,
          type: activityType,
          badge: activityBadge,
          icon: activityIcon,
          description: activityDescription
        })
      })
    }

    return activities
  }

  // Render the activities list in the dropdown
  renderActivitiesList(activities) {
    const groupedByPhase = this.groupByPhase(activities)
    const phaseNames = Object.keys(groupedByPhase)

    let html = '<div class="flex flex-col w-full max-h-[300px] overflow-y-auto px-2 py-2">'

    phaseNames.forEach(phaseName => {
      // Always show phase header
      // Get phase number from first activity in this group
      const firstActivity = groupedByPhase[phaseName][0]

      // Format phase display name based on whether we have phase number
      const phaseDisplayName = firstActivity.phaseNumber
        ? `Phase ${firstActivity.phaseNumber}. ${phaseName}`
        : phaseName

      html += `
        <div class="px-3 py-2 bg-[#F9FAFB] border-b border-[#E4E5E8]">
          <span class="font-semibold text-xs leading-5 text-[#6B7280]">${this.escapeHtml(phaseDisplayName)}</span>
        </div>
      `

      groupedByPhase[phaseName].forEach(activity => {
        // Check if activity is selected by both ID and metadata (for robustness after reordering)
        let isSelected = this.selectedActivities.includes(activity.id)

        // Also check by metadata in case ID changed
        if (!isSelected) {
          const key = `${activity.title}|${activity.phaseNumber || ''}`
          isSelected = Array.from(this.selectedActivityMetadata.values()).some(
            metadata => `${metadata.title}|${metadata.phaseNumber || ''}` === key
          )
        }

        const checkboxId = `activity-checkbox-${activity.id}`

        // Format activity display name
        // For multiple phases: show activity number + activity name
        // For single phase: show activity number + activity name
        const displayName = activity.activityNumber
          ? `${activity.activityNumber}. ${this.escapeHtml(activity.title)}`
          : this.escapeHtml(activity.title)

        html += `
          <div class="flex items-center gap-2 px-3 py-2.5 hover:bg-[#FAFAFB] cursor-pointer border-b border-[#F2F3F5]"
               data-action="click->activity-selector#selectActivity"
               data-activity-id="${activity.id}"
               data-activity-title="${this.escapeHtml(activity.title)}"
               data-activity-number="${activity.activityNumber || ''}"
               data-activity-type="${activity.type || ''}"
               data-activity-badge="${activity.badge || ''}"
               data-activity-icon="${activity.icon || ''}"
               data-activity-description="${this.escapeHtml(activity.description || '')}"
               data-phase-number="${activity.phaseNumber || ''}">
            <!-- Checkbox component structure -->
            <div class="flex flex-row justify-center items-center pt-0.5">
              <div class="relative inline-flex">
                <input type="checkbox"
                       id="${checkboxId}"
                       class="peer appearance-none transition-all w-4 h-4 rounded border border-[#E4E5E8] bg-white hover:border-[#E4E5E8] checked:bg-[#5A36E6] checked:border-[#5A36E6] focus:outline-none focus:shadow-[0_0_0_2px_#FFFFFF,0_0_0_4px_#7147FF] cursor-pointer"
                       ${isSelected ? 'checked' : ''}>
                <div class="pointer-events-none absolute inset-0 flex items-center justify-center text-white opacity-0 peer-checked:opacity-100">
                  <svg class="w-3 h-3" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M10 3L4.5 8.5L2 6" stroke="currentColor" stroke-width="1.67" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </div>
              </div>
            </div>
            <span class="text-sm leading-5 text-[#1F2937]">${displayName}</span>
          </div>
        `
      })
    })

    html += '</div>'

    this.listTarget.innerHTML = html
  }

  // Render empty state
  renderEmptyState() {
    this.listTarget.innerHTML = `
      <div class="flex flex-col items-center justify-center gap-2 p-6 text-center">
        <div class="w-12 h-12 bg-[#F9FAFB] rounded-full flex items-center justify-center">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M8 6H21M8 12H21M8 18H21M3 6H3.01M3 12H3.01M3 18H3.01" stroke="#6B7280" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </div>
        <span class="text-sm font-medium text-[#6B7280]">No previous activities</span>
        <span class="text-xs text-[#9CA3AF]">Add activities in previous phases first</span>
      </div>
    `
  }

  // Group activities by phase
  groupByPhase(activities) {
    return activities.reduce((acc, activity) => {
      if (!acc[activity.phaseName]) {
        acc[activity.phaseName] = []
      }
      acc[activity.phaseName].push(activity)
      return acc
    }, {})
  }

  // Select/deselect an activity
  selectActivity(event) {
    event.stopPropagation()
    const activityId = event.currentTarget.dataset.activityId
    const checkbox = event.currentTarget.querySelector('input[type="checkbox"]')

    if (this.selectedActivities.includes(activityId)) {
      // Deselect: remove from array and uncheck
      this.selectedActivities = this.selectedActivities.filter(id => id !== activityId)
      this.selectedActivityMetadata.delete(activityId)
      checkbox.checked = false

      // Dispatch event to remove the activity chip
      this.dispatch("activityDeselected", {
        detail: { activityId: activityId }
      })
    } else {
      // Select: add to array and check
      this.selectedActivities.push(activityId)
      checkbox.checked = true

      // Store metadata for this activity
      this.selectedActivityMetadata.set(activityId, {
        title: event.currentTarget.dataset.activityTitle,
        phaseNumber: event.currentTarget.dataset.phaseNumber
      })

      // Dispatch event to add the activity chip
      const activityData = {
        id: activityId,
        title: event.currentTarget.dataset.activityTitle,
        activityNumber: event.currentTarget.dataset.activityNumber,
        type: event.currentTarget.dataset.activityType,
        badge: event.currentTarget.dataset.activityBadge,
        icon: event.currentTarget.dataset.activityIcon,
        description: event.currentTarget.dataset.activityDescription,
        phaseNumber: event.currentTarget.dataset.phaseNumber
      }

      this.dispatch("activitySelected", {
        detail: { activity: activityData }
      })
    }
  }

  // Escape HTML to prevent XSS
  escapeHtml(text) {
    if (!text) return ''
    const map = {
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#039;'
    }
    return text.replace(/[&<>"']/g, m => map[m])
  }
}
